// Considered: MultiSelectAtom (the enum/person value editor) — rejected because its
// options must be a closed, already-known list, and a reference's candidate set is
// every sphere of the target type: it is searched, paged and permission-filtered
// server-side. Considered: SphereReferenceInputMolecule — rejected because it is
// single-select by contract (a DropdownAtom writing one id) and its `property` prop
// is a `SchemaProperty`, which a filter catalog entry is not. This composes the two
// pieces that already exist for exactly this job: `SphereSelectDialogOrganism` (the
// searchable, scoped, permission-filtered sphere picker the relationship lane uses)
// and `SphereRefChipMolecule` (the id → name read-back); the "choose" affordance is a
// `ButtonAtom` (`secondary`, `small`), not a control of its own.
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import type { FilterExpression } from 'types/filters';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import SphereSelectDialogOrganism from '../../organisms/marketplace/SphereSelectDialogOrganism';
import type { SphereSearchResult } from '../../../hooks/useSphereSearch';
import SphereRefChipMolecule from '../SphereRefChip/SphereRefChipMolecule';
import './SphereMultiSelectValueMolecule.scss';

export interface SphereMultiSelectValueMoleculeProps {
  /** The chosen target sphere ids — the `values` array an `in` / `not_in` row carries. */
  value: string[];
  onChange: (next: string[]) => void;
  /**
   * `system_name` of the schema the reference points at
   * (`SchemaProperty.metadata.targetSchemaSystemName`, carried onto the catalog
   * entry as `PropertyOption.referenceTargetSchema`). Scopes the picker to
   * candidates the property could actually hold. Absent — the reference declares
   * no target type — the picker stays open to every sphere rather than silently
   * showing none.
   */
  targetSchemaSystemName?: string;
  readOnly?: boolean;
  /** Accessible name for the chip group, so the list of chosen spheres is announced as one thing. */
  ariaLabel?: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * The value editor for a reference property compared by set membership
 * (`in` / `not_in` — the prototype's "is any of" / "is none of"). The stored
 * operand is the target sphere's identifier, so the control picks spheres and
 * writes their ids; it never free-texts an id.
 */
const SphereMultiSelectValueMolecule: React.FC<SphereMultiSelectValueMoleculeProps> = ({
  value,
  onChange,
  targetSchemaSystemName,
  readOnly = false,
  ariaLabel,
  className = '',
  'data-testid': dataTestId = 'sphere-multi-select-value',
}) => {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);

  // The reference's declared target type is the candidate scope. Memoised because
  // `SphereSelectDialogOrganism` passes it straight into `useSphereSearch`, which
  // requires a stable reference or it re-fires the query on every render.
  const scopeFilters: FilterExpression[] = useMemo(
    () => (targetSchemaSystemName ? [{ property: 'type', operator: 'equals', value: targetSchemaSystemName }] : []),
    [targetSchemaSystemName]
  );

  const addSphere = useCallback(
    (sphere: SphereSearchResult): boolean => {
      if (value.includes(sphere.id)) return false;
      onChange([...value, sphere.id]);
      return true;
    },
    [value, onChange]
  );

  const removeSphere = useCallback((id: string) => onChange(value.filter((v) => v !== id)), [value, onChange]);

  return (
    <div className={`sphere-multi-select-value-molecule ${className}`.trim()} data-testid={dataTestId}>
      <div
        className="sphere-multi-select-value-molecule__chips"
        role="group"
        aria-label={ariaLabel ?? t('filterEditor.panel.spheres.chosenLabel')}
      >
        {value.length === 0 ? (
          // An empty selection is an INCOMPLETE row, not "any sphere":
          // `isFilterExpressionComplete` drops an `in` with no values from the
          // predicate, so the empty state must read as "nothing chosen yet".
          <span className="sphere-multi-select-value-molecule__empty">
            {t('filterEditor.panel.spheres.noneChosen')}
          </span>
        ) : (
          value.map((id) => (
            <SphereRefChipMolecule
              key={id}
              sphereId={id}
              readOnly={readOnly}
              onRemove={() => removeSphere(id)}
              className="sphere-multi-select-value-molecule__chip"
            />
          ))
        )}
      </div>
      {!readOnly && (
        <ButtonAtom
          type="button"
          variant="secondary"
          size="small"
          icon="pi pi-plus"
          label={t('filterEditor.panel.spheres.chooseSpheres')}
          onClick={() => setPickerOpen(true)}
          data-testid={`${dataTestId}-choose`}
        />
      )}

      {pickerOpen && (
        <SphereSelectDialogOrganism
          open
          onClose={() => setPickerOpen(false)}
          titleKey="filterEditor.panel.spheres.pickerTitle"
          onSelect={addSphere}
          excludedSphereIds={value}
          fixedContextFilters={scopeFilters}
          permission="view"
          showContextSelector={false}
        />
      )}
    </div>
  );
};

export default SphereMultiSelectValueMolecule;
